import { useCallback, useEffect, useRef } from "react";
import { FilesetResolver, PoseLandmarker } from "@mediapipe/tasks-vision";
import { createPushupEngine, elbowAngle, type PushupEngine } from "@pushup/detection";
import { useWorkoutStore } from "@/state/workout-store";
import { mediaPipeToPoseFrame, MIN_VISIBILITY } from "./landmark-mapping";

const MEDIAPIPE_VISION_CDN = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/wasm";
const MODEL_ASSET_PATH =
  "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/1/pose_landmarker_full.task";

export interface UsePoseDetectionOptions {
  mode?: "full" | "knee";
  /**
   * Whether to request the microphone together with the camera (used for clip
   * recording in celebrity mode). May be a getter so the value is read at the
   * moment `start()` runs rather than when the hook mounts.
   */
  audio?: boolean | (() => boolean);
}

export function usePoseDetection(options: UsePoseDetectionOptions = {}) {
  const { mode = "full", audio = false } = options;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const landmarkerRef = useRef<PoseLandmarker | null>(null);
  const engineRef = useRef<PushupEngine | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const hasAudioRef = useRef(false);
  const lastCameraErrorRef = useRef<Error | null>(null);
  const animFrameIdRef = useRef<number | null>(null);
  const lastVideoTimeRef = useRef<number>(-1);
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerStartedRef = useRef(false);
  /** Most recent detected pose, kept for the recording composite to redraw. */
  const lastLandmarksRef = useRef<ReadonlyArray<{
    x: number;
    y: number;
    visibility?: number;
  }> | null>(null);

  const {
    setStatus,
    setError,
    pushEngineEvent,
    updateDepth,
    updateArmDepths,
    tickElapsed,
    resetWorkout,
    setPushupsStarted,
  } = useWorkoutStore();

  const stop = useCallback(
    (options?: { keepStatus?: boolean }) => {
      if (animFrameIdRef.current !== null) {
        cancelAnimationFrame(animFrameIdRef.current);
        animFrameIdRef.current = null;
      }

      if (timerIntervalRef.current !== null) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      timerStartedRef.current = false;

      if (streamRef.current) {
        for (const track of streamRef.current.getTracks()) {
          track.stop();
        }
        streamRef.current = null;
      }
      hasAudioRef.current = false;
      lastLandmarksRef.current = null;

      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      if (landmarkerRef.current) {
        landmarkerRef.current.close();
        landmarkerRef.current = null;
      }

      if (engineRef.current) {
        engineRef.current.destroy();
        engineRef.current = null;
      }

      if (!options?.keepStatus) {
        setStatus("idle");
      }
    },
    [setStatus],
  );

  const start = useCallback(async () => {
    stop();
    resetWorkout();

    if (typeof window === "undefined") {
      return;
    }

    if (!window.isSecureContext) {
      setError("Camera access requires a secure context (HTTPS or http://localhost).");
      return;
    }

    if (!navigator?.mediaDevices?.getUserMedia) {
      setError("Your browser does not support camera access via getUserMedia.");
      return;
    }

    setStatus("loading");

    // 1. Request camera (and optionally mic) first so the browser permission
    // prompt appears immediately. If the mic is requested but unavailable or
    // denied, fall back to video-only so the workout still works.
    const audioEnabled = typeof audio === "function" ? audio() : audio;

    const requestStream = async (withAudio: boolean): Promise<MediaStream | null> => {
      try {
        return await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
          audio: withAudio,
        });
      } catch {
        try {
          // Fallback to basic video constraint if facingMode constraint failed
          // (e.g. desktop webcam).
          return await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: withAudio,
          });
        } catch (err) {
          lastCameraErrorRef.current = err as Error;
          return null;
        }
      }
    };

    let stream = await requestStream(Boolean(audioEnabled));
    if (!stream && audioEnabled) {
      // Mic denied/unavailable — continue without audio.
      stream = await requestStream(false);
      hasAudioRef.current = false;
    }
    if (!stream) {
      const err = lastCameraErrorRef.current;
      if (err?.name === "NotAllowedError" || err?.name === "PermissionDeniedError") {
        setError(
          "Camera permission was denied. Please grant camera access in your browser settings.",
        );
      } else if (err?.name === "NotFoundError" || err?.name === "DevicesNotFoundError") {
        setError("No camera device found on this system.");
      } else {
        setError(`Camera error: ${err?.message || "Failed to access camera"}`);
      }
      return;
    }

    streamRef.current = stream;
    hasAudioRef.current = stream.getAudioTracks().length > 0;

    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      try {
        await videoRef.current.play();
      } catch (playErr) {
        console.warn("Video play interrupted", playErr);
      }
    }

    // 2. Load MediaPipe PoseLandmarker on-device vision model
    try {
      const vision = await FilesetResolver.forVisionTasks(MEDIAPIPE_VISION_CDN);

      let landmarker: PoseLandmarker;
      try {
        landmarker = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: MODEL_ASSET_PATH,
            delegate: "GPU",
          },
          runningMode: "VIDEO",
          numPoses: 1,
        });
      } catch (gpuError) {
        console.warn("GPU delegate failed for PoseLandmarker, falling back to CPU", gpuError);
        landmarker = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: MODEL_ASSET_PATH,
            delegate: "CPU",
          },
          runningMode: "VIDEO",
          numPoses: 1,
        });
      }

      landmarkerRef.current = landmarker;

      engineRef.current = createPushupEngine({ mode });
      setStatus("active");

      // The elapsed timer only starts once the user begins their first pushup
      // (first "descent" into a rep) — not while they are simply standing ready.
      const startElapsedTimer = () => {
        if (timerStartedRef.current) {
          return;
        }
        timerStartedRef.current = true;
        let lastTick = performance.now();
        timerIntervalRef.current = setInterval(() => {
          const now = performance.now();
          tickElapsed(Math.round(now - lastTick));
          lastTick = now;
        }, 500);
      };

      // Animation loop for detection
      const processFrame = () => {
        const video = videoRef.current;
        const landmarker = landmarkerRef.current;
        const engine = engineRef.current;

        if (video && landmarker && engine && video.currentTime !== lastVideoTimeRef.current) {
          lastVideoTimeRef.current = video.currentTime;
          const timestamp = performance.now();

          const results = landmarker.detectForVideo(video, timestamp);

          if (results.landmarks && results.landmarks.length > 0) {
            const firstPose = results.landmarks[0]!;
            lastLandmarksRef.current = firstPose;
            const poseFrame = mediaPipeToPoseFrame(firstPose, timestamp);

            const events = engine.feedFrame(poseFrame);
            for (const event of events) {
              pushEngineEvent(event);
              if (!timerStartedRef.current && event.type === "state" && event.state === "descent") {
                startElapsedTimer();
                setPushupsStarted(true);
              }
            }

            updateDepth(Math.round(engine.currentDepthDeg));
            updateArmDepths(armDepth(firstPose, 11, 13, 15), armDepth(firstPose, 12, 14, 16));
            drawSkeleton(firstPose);
          } else {
            // No pose detected in frame
            lastLandmarksRef.current = null;
            const poseFrame = { timestamp, landmarks: {} };
            const events = engine.feedFrame(poseFrame);
            for (const event of events) {
              pushEngineEvent(event);
            }
            clearCanvas();
          }
        }

        animFrameIdRef.current = requestAnimationFrame(processFrame);
      };

      animFrameIdRef.current = requestAnimationFrame(processFrame);
    } catch (err) {
      console.error("Failed to initialize pose detection", err);
      const message =
        err instanceof Error
          ? err.message
          : typeof err === "string"
            ? err
            : "Failed to initialize MediaPipe pose detection model.";
      setError(message);
      stop();
    }
  }, [
    mode,
    audio,
    resetWorkout,
    setError,
    setStatus,
    stop,
    pushEngineEvent,
    updateDepth,
    updateArmDepths,
    tickElapsed,
    setPushupsStarted,
  ]);

  const drawSkeleton = (
    landmarks: ReadonlyArray<{ x: number; y: number; visibility?: number }>,
  ) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const connections: Array<[number, number]> = [
      // Left arm
      [11, 13],
      [13, 15],
      // Right arm
      [12, 14],
      [14, 16],
      // Shoulders
      [11, 12],
      // Hips
      [23, 24],
      // Torso
      [11, 23],
      [12, 24],
      // Left leg
      [23, 25],
      [25, 27],
      // Right leg
      [24, 26],
      [26, 28],
    ];

    const w = canvas.width;
    const h = canvas.height;

    // Draw lines
    ctx.lineWidth = 4;
    ctx.strokeStyle = "rgba(16, 185, 129, 0.8)"; // emerald

    for (const [i, j] of connections) {
      const p1 = landmarks[i];
      const p2 = landmarks[j];

      if (p1 && p2 && (p1.visibility ?? 1) >= 0.5 && (p2.visibility ?? 1) >= 0.5) {
        ctx.beginPath();
        ctx.moveTo(p1.x * w, p1.y * h);
        ctx.lineTo(p2.x * w, p2.y * h);
        ctx.stroke();
      }
    }

    // Highlight key joints
    for (let i = 0; i < landmarks.length; i++) {
      const p = landmarks[i];
      if (!p || (p.visibility ?? 1) < 0.5) continue;

      const px = p.x * w;
      const py = p.y * h;

      // Highlight elbows (13: LeftElbow, 14: RightElbow) specifically
      if (i === 13 || i === 14) {
        ctx.beginPath();
        ctx.arc(px, py, 10, 0, 2 * Math.PI);
        ctx.fillStyle = "#f59e0b"; // amber for elbows
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#ffffff";
        ctx.stroke();
      } else if ([11, 12, 15, 16, 23, 24, 25, 26, 27, 28].includes(i)) {
        ctx.beginPath();
        ctx.arc(px, py, 6, 0, 2 * Math.PI);
        ctx.fillStyle = "#10b981"; // emerald
        ctx.fill();
      }
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const armDepth = (
    landmarks: ReadonlyArray<{ x: number; y: number; visibility?: number }>,
    shoulder: number,
    elbow: number,
    wrist: number,
  ): number | null => {
    const s = landmarks[shoulder];
    const e = landmarks[elbow];
    const w = landmarks[wrist];
    if (!s || !e || !w) return null;
    const vis =
      (s.visibility ?? 0) >= MIN_VISIBILITY &&
      (e.visibility ?? 0) >= MIN_VISIBILITY &&
      (w.visibility ?? 0) >= MIN_VISIBILITY;
    if (!vis) return null;
    return Math.round(
      elbowAngle(
        { x: s.x, y: s.y, visibility: s.visibility ?? 0 },
        { x: e.x, y: e.y, visibility: e.visibility ?? 0 },
        { x: w.x, y: w.y, visibility: w.visibility ?? 0 },
      ),
    );
  };

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  return {
    videoRef,
    canvasRef,
    start,
    stop,
    /** The live camera/mic stream, or null when no camera is active. */
    getStream: () => streamRef.current,
    /** The most recent detected pose landmarks (normalized), or null. */
    getLastLandmarks: () => lastLandmarksRef.current,
    /** Whether the active stream carries a microphone audio track. */
    hasAudio: () => hasAudioRef.current,
  };
}
